// src/pages/IntelPosts.jsx
import { Fragment, useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import ThreatFeedApiCta from "../components/ThreatFeedApiCta";
import { Search as SearchIcon, X, ShieldAlert, Zap, Bug, Globe, Users, Activity, Target, Calendar, Menu } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  ensureAuthOrRedirect,
  attachExpiryTimer,
  redirectToLogin,
} from "../utils/auth";
import SearchableSelect from "../components/SearchableSelect";
import IntelDetailSidebar from "../components/DetailSidebarIntel";
import IntelNewsDetailSidebar from "../components/DetailSidebarNews";
import DetailSidebarIOC from "../components/DetailSidebarIOC";
import DetailSidebarActor from "../components/DetailSidebarActor";
import ThreatFeedDashboard from "./ThreatFeedDashboard";
import {
  FilterBar,
  FilterDivider,
  FilterSegment,
  FilterMenu,
  FilterMenuItem,
  FilterInput,
  FilterReset,
} from "../components/FilterToolbar";

// Backends
const INTEL_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/get_feed.php";
const DIM_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/intel_dimensions.php";
const NEWS_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/get_news.php";
const IOC_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/ioc_feeds.php";
const IOC_DIM_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/ioc_dimensions.php";
const THREAT_FEED_TOKEN_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/get_token.php";
const ACTOR_API = "https://whiteintel.io/api/regular/app/v2/threatfeed/threat_actors.php";

const NEWS_TAG_OPTIONS = [
  "Data Breach",
  "Vulnerability Intelligence",
  "Exploit Activity",
  "Ransomware",
  "Malware",
  "Infostealer",
  "Attack Vector",
  "Supply Chain Risk",
  "Credential Theft",
  "Phishing",
  "Cloud Security",
  "Critical Infrastructure",
  "Nation-State Activity",
  "Law Enforcement / Takedown",
  "Dark Web Activity",
  "Privacy & Compliance",
  "General Cybersecurity News",
];

// UI styles
const frosted = "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";
const chip = "inline-flex items-center rounded-full px-3 py-1.5 text-sm border transition-colors";

const tagClass = (t) => {
  switch (t) {
    case "telegram": return "border-sky-500/30 text-sky-200 bg-sky-500/10";
    case "ransomware": return "border-red-500/30 text-red-200 bg-red-500/10";
    case "ddos": case "ddos attack": return "border-amber-500/30 text-amber-200 bg-amber-500/10";
    case "dataleak": case "data breach": case "data leak": return "border-emerald-500/30 text-emerald-200 bg-emerald-500/10";
    default: return "border-neutral-700 text-neutral-200 bg-neutral-900/40";
  }
};

const iocTagClass = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("ip")) return "border-orange-500/30 text-orange-200 bg-orange-500/10";
  if (t.includes("domain")) return "border-indigo-500/30 text-indigo-200 bg-indigo-500/10";
  if (t.includes("hash") || t.includes("sha")) return "border-purple-500/30 text-purple-200 bg-purple-500/10";
  if (t.includes("url")) return "border-blue-500/30 text-blue-200 bg-blue-500/10";
  return "border-neutral-700 text-neutral-200 bg-neutral-900/40";
};

const fmtISO = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");

// ----- date helpers for FEED (not for chart) -----
const getDateDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0];
};
const resolveRange = (quick) => {
  const today = new Date().toISOString().split("T")[0];
  if (quick === "7d") return { date_from: getDateDaysAgo(7), date_to: today };
  if (quick === "30d") return { date_from: getDateDaysAgo(30), date_to: today };
  if (quick === "90d") return { date_from: getDateDaysAgo(90), date_to: today };
  if (quick === "All") return { date_from: getDateDaysAgo(1080), date_to: today };
  return { date_from: getDateDaysAgo(180), date_to: today };
};

// ----- fill all-time buckets for chart -----
function fillAllTime(buckets = [], per = "day") {
  if (!Array.isArray(buckets) || buckets.length === 0) return [];

  const norm = buckets.map((b) => ({
    label: String(b.label),
    count: Number(b.count ?? b.cnt ?? 0),
  }));
  const map = new Map(norm.map((b) => [b.label, b.count]));

  const parseDay = (label) => new Date(label + "T00:00:00Z");
  const fmtDay = (d) => d.toISOString().slice(0, 10);
  const stepDay = (d) => d.setUTCDate(d.getUTCDate() + 1);

  const parseMonth = (label) => {
    const [y, m] = label.split("-").map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, 1));
  };
  const fmtMonth = (d) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  const stepMonth = (d) => d.setUTCMonth(d.getUTCMonth() + 1);

  const parseWeek = (label) => {
    const [y, wStr] = label.split("-W");
    const year = Number(y);
    const week = Number(wStr);
    const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
    const dow = simple.getUTCDay() || 7;
    const monday = new Date(simple);
    monday.setUTCDate(simple.getUTCDate() - (dow - 1));
    return monday;
  };
  const fmtWeek = (d) => {
    const tmp = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    );
    const dayNum = (tmp.getUTCDay() + 6) % 7;
    tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
    const firstThu = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
    const week = 1 + Math.round(((tmp - firstThu) / 86400000 - 3) / 7);
    return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
  };
  const stepWeek = (d) => d.setUTCDate(d.getUTCDate() + 7);

  let parse, fmt, step;
  if (per === "month") {
    parse = parseMonth;
    fmt = fmtMonth;
    step = stepMonth;
  } else if (per === "week") {
    parse = parseWeek;
    fmt = fmtWeek;
    step = stepWeek;
  } else {
    parse = parseDay;
    fmt = fmtDay;
    step = stepDay;
  }

  const sorted = [...norm].sort((a, b) => parse(a.label) - parse(b.label));
  const start = parse(sorted[0].label);
  const end = parse(sorted[sorted.length - 1].label);

  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    const label = fmt(cur);
    out.push({ label, count: map.get(label) ?? 0 });
    step(cur);
  }
  return out;
}

export default function IntelPosts() {
  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showFilters, setShowFilters] = useState(false);

  // URL <-> tab sync: read on mount, write on change. Lets users bookmark or
  // refresh on a specific tab. Also accepts a `category` deep-link param so
  // dashboards can hand off a pre-filtered chatter view (e.g. ransomware-only).
  const VALID_TABS = ["overview", "chatters", "actors", "news", "ioc"];
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && VALID_TABS.includes(t)) {
      setActiveTab(t);
    }
    const c = params.get("category");
    if (c) {
      setCategory(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTab && activeTab !== "overview") {
      params.set("tab", activeTab);
    } else {
      params.delete("tab");
    }
    const qs = params.toString();
    const newUrl =
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, [activeTab]);

  // ===== CHATTERS (Threat Feed) list & paging =====
  const [chatterPosts, setChatterPosts] = useState([]);
  const [chatterPage, setChatterPage] = useState(1);
  const [chatterHasMore, setChatterHasMore] = useState(true);
  const [chatterLoading, setChatterLoading] = useState(false);
  const [chatterError, setChatterError] = useState("");

  // ===== NEWS list & paging =====
  const [newsPosts, setNewsPosts] = useState([]);
  const [newsPage, setNewsPage] = useState(1);
  const [newsHasMore, setNewsHasMore] = useState(true);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState("");

  // ===== IOC list & paging =====
  const [iocPosts, setIocPosts] = useState([]);
  const [iocPage, setIocPage] = useState(1);
  const [iocHasMore, setIocHasMore] = useState(true);
  const [iocLoading, setIocLoading] = useState(false);
  const [iocError, setIocError] = useState("");

  // ===== ACTORS (New) list & paging =====
  const [actorPosts, setActorPosts] = useState([]);
  const [actorPage, setActorPage] = useState(1);
  const [actorHasMore, setActorHasMore] = useState(true);
  const [actorLoading, setActorLoading] = useState(false);
  const [actorError, setActorError] = useState("");
  const [actorStats, setActorStats] = useState(null);

  // ===== chart (all-time) =====
  const [perBucket, setPerBucket] = useState("month");

  const [chatterChartData, setChatterChartData] = useState([]);
  const [chatterChartLoading, setChatterChartLoading] = useState(false);
  const [chatterChartError, setChatterChartError] = useState("");
  const chatterChartInflightKeyRef = useRef("");

  const [newsChartData, setNewsChartData] = useState([]);
  const [newsChartLoading, setNewsChartLoading] = useState(false);
  const [newsChartError, setNewsChartError] = useState("");
  const newsChartInflightKeyRef = useRef("");
  const [actorTtp, setActorTtp] = useState("All");

  const [iocChartData, setIocChartData] = useState([]);
  const [iocChartLoading, setIocChartLoading] = useState(false);
  const [iocChartError, setIocChartError] = useState("");
  const iocChartInflightKeyRef = useRef("");

  // filters (Chatters)
  const [dateQuick, setDateQuick] = useState("All"); 
  const [network, setNetwork] = useState("all");
  const [category, setCategory] = useState(""); 
  const [industry, setIndustry] = useState("all");
  const [country, setCountry] = useState("");
  const [domain, setDomain] = useState("");
  const [site, setSite] = useState("");
  const [actor, setActor] = useState(""); 
  const [tag, setTag] = useState("");
  const [hasScreens, setHasScreens] = useState(false);
  const [hasURL, setHasURL] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  // filters (IOC)
  const [iocType, setIocType] = useState("");
  const [threatType, setThreatType] = useState("");
  const [reporter, setReporter] = useState("");
  const [malware, setMalware] = useState("");
  
  // filters (Actors - New)
  const [actorStatus, setActorStatus] = useState("");
  const [actorSort, setActorSort] = useState("activity");

  // searchable dropdown options
  const [networkOpts, setNetworkOpts] = useState([]);
  const [industryOpts, setIndustryOpts] = useState([]);
  const [categoryOpts, setCategoryOpts] = useState([]);
  const [actorOpts, setActorOpts] = useState([]);
  
  // IOC specific dropdown options
  const [iocTypeOpts, setIocTypeOpts] = useState([]);
  const [threatTypeOpts, setThreatTypeOpts] = useState([]);
  const [reporterOpts, setReporterOpts] = useState([]);
  const [malwareOpts, setMalwareOpts] = useState([]);

  const [dimLoading, setDimLoading] = useState(false);
  const [dimError, setDimError] = useState("");

  // search bar (shared)
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");

  // detail sidebars
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [newsDetailOpen, setNewsDetailOpen] = useState(false);
  const [selectedNewsId, setSelectedNewsId] = useState(null);

  const [iocDetailOpen, setIocDetailOpen] = useState(false);
  const [selectedIocId, setSelectedIocId] = useState(null);
  
  const [actorDetailOpen, setActorDetailOpen] = useState(false);
  const [selectedActorSlug, setSelectedActorSlug] = useState(null);

  // infinite scroll sentinels
  const chatterLoadMoreRef = useRef(null);
  const newsLoadMoreRef = useRef(null);
  const iocLoadMoreRef = useRef(null);
  const actorLoadMoreRef = useRef(null);

  // ===== Firewall Integration Modal (IOC) =====
  const [integrateOpen, setIntegrateOpen] = useState(false);
  const [tfToken, setTfToken] = useState("");
  const [tfTokenLoading, setTfTokenLoading] = useState(false);
  const [tfTokenError, setTfTokenError] = useState("");
  const [downloadType, setDownloadType] = useState("ip");
  const [downloadTime, setDownloadTime] = useState("24h");
  const buildDownloadUrl = (type, time) => {
    if (!tfToken) return "";
    return `https://whiteintel.io/api/regular/app/v2/threatfeed/feed_download.php?token=${tfToken}&type=${type}&time=${time}`;
  };


  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        return true;
      } catch {
        return false;
      }
    }
  };

  const fetchThreatFeedToken = async () => {
    setTfTokenLoading(true);
    setTfTokenError("");
    try {
      const res = await fetch(THREAT_FEED_TOKEN_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ scope: "threat_feed" }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error("Redirect");
      }
      if (!res.ok) throw new Error(`Token HTTP ${res.status}`);

      const j = await res.json();
      const token =
        j?.token ||
        j?.data?.token ||
        j?.data?.threat_feed_token ||
        j?.threat_feed_token ||
        "";

      if (!token) throw new Error("Token missing in response");
      setTfToken(token);
    } catch (e) {
      setTfToken("");
      setTfTokenError(e?.message || "Failed to retrieve token");
    } finally {
      setTfTokenLoading(false);
    }
  };


  // auth + timers
  useEffect(() => {
    ensureAuthOrRedirect();
    attachExpiryTimer();
  }, []);

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const resetFilters = () => {
    setNetwork("all");
    setActorTtp("All");
    setCategory("");
    setIndustry("all");
    setCountry("");
    setDomain("");
    setSite("");
    setActor("");
    setTag("");
    setHasScreens(false);
    setHasURL(false);
    setHasContent(false);
    setIocType("");
    setThreatType("");
    setReporter("");
    setMalware("");
    setActorStatus("");
    setActorSort("activity");
  };

  // ----- dimensions (Chatters) -----
  useEffect(() => {
    if (activeTab !== "chatters") return;
    let cancelled = false;
    (async () => {
      setDimLoading(true);
      setDimError("");
      try {
        const token = getJwtToken();
        const { date_from, date_to } = resolveRange(dateQuick);

        const make = (metric, extra = {}) =>
          fetch(DIM_API, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ metric, date_from, date_to, limit: 500, ...extra }),
          }).then(async (r) => {
            if ([401, 419, 440].includes(r.status)) {
              redirectToLogin();
              throw new Error(`AUTH_REDIRECT_${r.status}`);
            }
            if (!r.ok) throw new Error(`${metric} HTTP ${r.status}`);
            const j = await r.json();
            const arr = j?.data?.items ?? j?.items ?? j?.rows ?? j ?? [];
            return arr
              .map((x) =>
                typeof x === "string"
                  ? x
                  : x?.value ?? x?.name ?? x?.network ?? x?.industry ?? ""
              )
              .filter(Boolean);
          });

        const [nets, inds, cats, actors] = await Promise.all([
          make("networks"),
          make("industries"),
          make("categories"),
          make("actors"),
        ]);

        if (!cancelled) {
          setNetworkOpts(nets.sort((a, b) => a.localeCompare(b)));
          setIndustryOpts(inds.sort((a, b) => a.localeCompare(b)));
          setCategoryOpts(cats.sort((a, b) => a.localeCompare(b)));
          setActorOpts(actors.sort((a, b) => a.localeCompare(b)));
        }
      } catch (e) {
        if (!cancelled) setDimError(e.message || "Failed to load filters");
      } finally {
        if (!cancelled) setDimLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, dateQuick]);

  // ----- dimensions (IOC) -----
  useEffect(() => {
    if (activeTab !== "ioc") return;
    let cancelled = false;
    (async () => {
      setDimLoading(true);
      setDimError("");
      try {
        const token = getJwtToken();
        const { date_from } = resolveRange(dateQuick);

        const make = (metric) =>
          fetch(IOC_DIM_API, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ metric, date_from, limit: 200 }),
          }).then(async (r) => {
             if ([401, 419, 440].includes(r.status)) {
               redirectToLogin();
               throw new Error("AUTH_REDIRECT");
             }
             if (!r.ok) throw new Error(`${metric} HTTP ${r.status}`);
             const j = await r.json();
             const arr = j?.data?.items ?? [];
             return arr.map(x => x.value).filter(Boolean);
          });

        const [itypes, ttypes, reps, mals] = await Promise.all([
            make("ioc_types"),
            make("threat_types"),
            make("reporters"),
            make("malware")
        ]);

        if (!cancelled) {
            setIocTypeOpts(itypes);
            setThreatTypeOpts(ttypes);
            setReporterOpts(reps);
            setMalwareOpts(mals);
        }
      } catch (e) {
         if (!cancelled) setDimError(e.message || "Failed to load IOC filters");
      } finally {
         if (!cancelled) setDimLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, dateQuick]);

  // ----- list mapping helpers -----
  const toChatterRow = (r) => ({
    id: r.id,
    uuid: r.uuid,
    title: r.title,
    published_at: r.published_at,
    tags: r.tags || (r.tags_csv ? r.tags_csv.split(",").map((x) => x.trim()).filter(Boolean) : []),
  });

  const toNewsRow = (r) => ({
    id: r.id,
    title: r.title,
    source: r.source || r.publisher || "",
    published_at: r.published_at || r.published_iso || null,
    tags: Array.isArray(r.tags) ? r.tags : [],
  });

  const toIocRow = (r) => ({
    id: r.id,
    ioc_value: r.ioc_value,
    ioc_type: r.ioc_type,
    threat_type: r.threat_type,
    malware: r.malware_printable,
    first_seen: r.first_seen_utc
  });

  const toActorRow = (r) => ({
    name: r.name,
    slug: r.slug,
    status: r.status,
    last_seen: r.last_seen,
    incidents: r.incidents,
    industries: r.industries || [],
    attack_types: r.attack_types || []
  });

  // ----- CHATTERS list fetch -----
  const chatterInflightKeyRef = useRef("");
  const fetchChatterPage = async ({ append = false } = {}) => {
    if (append && (!chatterHasMore || chatterLoading)) return;
    const { date_from, date_to } = resolveRange(dateQuick);
    const nextPage = append ? chatterPage + 1 : 1;
    const payload = {
      metric: "feed",
      page: nextPage,
      per_page: 50,
      q: q.trim() || undefined,
      date_from,
      date_to,
      has_screenshots: hasScreens || undefined,
      has_url: hasURL || undefined,
      has_content: hasContent || undefined,
    };
    if (network !== "all") payload.network = network;
    if (category.trim()) payload.category = category.trim();
    if (industry !== "all") payload.victim_industry = industry;
    if (country.trim()) payload.victim_country = country.trim();
    if (domain.trim()) payload.victim_domain = domain.trim();
    if (site.trim()) payload.victim_site = site.trim();
    if (actor.trim()) payload.actor = actor.trim().toLowerCase();
    if (tag.trim()) payload.tag = tag.trim().toLowerCase();

    const reqKey = JSON.stringify(payload);
    if (chatterInflightKeyRef.current === reqKey) return;
    chatterInflightKeyRef.current = reqKey;

    setChatterLoading(true);
    setChatterError("");

    try {
      const res = await fetch(INTEL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const rows = raw?.data?.results ?? raw?.data?.rows ?? [];
      const ui = rows.map(toChatterRow);
      setChatterPosts((prev) => (append ? [...prev, ...ui] : ui));
      setChatterPage(raw?.data?.page ?? nextPage);
      setChatterHasMore(Boolean(raw?.data?.has_more));
    } catch (e) {
      setChatterError(e.message);
    } finally {
      setChatterLoading(false);
      chatterInflightKeyRef.current = "";
    }
  };

  // ----- NEWS list fetch -----
  const newsInflightKeyRef = useRef("");
  const fetchNewsPage = async ({ append = false } = {}) => {
    if (append && (!newsHasMore || newsLoading)) return;
    const { date_from, date_to } = resolveRange(dateQuick);
    const nextPage = append ? newsPage + 1 : 1;
    const payload = {
      metric: "feed",
      page: nextPage,
      per_page: 50,
      q: q.trim() || undefined,
      date_from,
      date_to,
    };
    if (tag.trim()) payload.tag = tag.trim();

    const reqKey = JSON.stringify(payload);
    if (newsInflightKeyRef.current === reqKey) return;
    newsInflightKeyRef.current = reqKey;

    setNewsLoading(true);
    setNewsError("");

    try {
      const res = await fetch(NEWS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const rows = raw?.data?.results ?? raw?.data?.rows ?? [];
      const ui = rows.map(toNewsRow);
      setNewsPosts((prev) => (append ? [...prev, ...ui] : ui));
      setNewsPage(raw?.data?.page ?? nextPage);
      setNewsHasMore(Boolean(raw?.data?.has_more));
    } catch (e) {
      setNewsError(e.message);
    } finally {
      setNewsLoading(false);
      newsInflightKeyRef.current = "";
    }
  };

  // ----- IOC list fetch -----
  const iocInflightKeyRef = useRef("");
  const fetchIocPage = async ({ append = false } = {}) => {
    if (append && (!iocHasMore || iocLoading)) return;
    const nextPage = append ? iocPage + 1 : 1;
    
    const payload = {
        metric: "feed",
        page: nextPage,
        limit: 50,
        q: q.trim() || undefined,
    };
    if (iocType.trim()) payload.ioc_type = iocType;
    if (threatType.trim()) payload.threat_type = threatType;
    if (reporter.trim()) payload.reporter = reporter;
    if (malware.trim()) payload.malware = malware;

    const reqKey = JSON.stringify(payload);
    if (iocInflightKeyRef.current === reqKey) return;
    iocInflightKeyRef.current = reqKey;

    setIocLoading(true);
    setIocError("");

    try {
        const res = await fetch(IOC_API, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
            body: JSON.stringify(payload),
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
        if (res.status === 403) throw new Error("Access Denied: Threat Intel License Required");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        
        const raw = await res.json();
        const data = raw?.data ?? {};
        const rows = data.rows ?? [];
        const ui = rows.map(toIocRow);
        
        setIocPosts((prev) => (append ? [...prev, ...ui] : ui));
        setIocPage(data.page ?? nextPage);
        setIocHasMore(Boolean(data.has_more));
    } catch (e) {
        setIocError(e.message);
    } finally {
        setIocLoading(false);
        iocInflightKeyRef.current = "";
    }
  };

  // ----- ACTOR list fetch (NEW) -----
  const actorInflightKeyRef = useRef("");
  const fetchActorPage = async ({ append = false } = {}) => {
    if (append && (!actorHasMore || actorLoading)) return;
    const nextPage = append ? actorPage + 1 : 1;
    
    const payload = {
      metric: "directory",
      page: nextPage,
      limit: 20,
      q: q.trim() || undefined,
      status: actorStatus || undefined,
      sort: actorSort
    };

    if (actorTtp && actorTtp !== "All") {
        payload.ttp = actorTtp;
    }

    const reqKey = JSON.stringify(payload);
    if (actorInflightKeyRef.current === reqKey) return;
    actorInflightKeyRef.current = reqKey;

    setActorLoading(true);
    setActorError("");

    try {
        const res = await fetch(ACTOR_API, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
            body: JSON.stringify(payload),
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        
        const raw = await res.json();
        const data = raw?.data ?? {};
        const rows = data.rows ?? [];
        const ui = rows.map(toActorRow);
        
        setActorPosts((prev) => (append ? [...prev, ...ui] : ui));
        setActorPage(data.page ?? nextPage);
        setActorHasMore((data.page < data.total_pages));
    } catch (e) {
        setActorError(e.message);
    } finally {
        setActorLoading(false);
        actorInflightKeyRef.current = "";
    }
  };

  const fetchActorStats = async () => {
      try {
          const res = await fetch(ACTOR_API, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
              body: JSON.stringify({ metric: "stats" }),
          });
          const raw = await res.json();
          setActorStats(raw?.data);
      } catch (e) {
          console.error(e);
      }
  }

  // ----- fetch list when filters/search/tab change -----
  useEffect(() => {
    if (activeTab === "chatters") {
      setChatterPage(1);
      setChatterHasMore(true);
      fetchChatterPage({ append: false });
    } else if (activeTab === "news") {
      setNewsPage(1);
      setNewsHasMore(true);
      fetchNewsPage({ append: false });
    } else if (activeTab === "ioc") {
      setIocPage(1);
      setIocHasMore(true);
      fetchIocPage({ append: false });
    } else if (activeTab === "actors") {
      setActorPage(1);
      setActorHasMore(true);
      fetchActorPage({ append: false });
      fetchActorStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    q,
    dateQuick,
    network,
    category,
    industry,
    country,
    domain,
    site,
    actor,
    tag,
    hasScreens,
    hasURL,
    hasContent,
    iocType,
    threatType,
    reporter,
    malware,
    actorStatus,
    actorSort,
    actorTtp
  ]);

  // ----- infinite scroll sentinels -----
  useEffect(() => {
    if (activeTab !== "chatters" || !chatterLoadMoreRef.current || !chatterHasMore || chatterLoading || chatterPosts.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && chatterHasMore && !chatterLoading) fetchChatterPage({ append: true });
    }, { root: null, rootMargin: "300px", threshold: 0 });
    obs.observe(chatterLoadMoreRef.current);
    return () => obs.disconnect();
  }, [activeTab, chatterHasMore, chatterLoading, chatterPosts.length]);

  useEffect(() => {
    if (activeTab !== "news" || !newsLoadMoreRef.current || !newsHasMore || newsLoading || newsPosts.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && newsHasMore && !newsLoading) fetchNewsPage({ append: true });
    }, { root: null, rootMargin: "300px", threshold: 0 });
    obs.observe(newsLoadMoreRef.current);
    return () => obs.disconnect();
  }, [activeTab, newsHasMore, newsLoading, newsPosts.length]);

  useEffect(() => {
    if (activeTab !== "ioc" || !iocLoadMoreRef.current || !iocHasMore || iocLoading || iocPosts.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && iocHasMore && !iocLoading) fetchIocPage({ append: true });
    }, { root: null, rootMargin: "300px", threshold: 0 });
    obs.observe(iocLoadMoreRef.current);
    return () => obs.disconnect();
  }, [activeTab, iocHasMore, iocLoading, iocPosts.length]);

  useEffect(() => {
    if (activeTab !== "actors" || !actorLoadMoreRef.current || !actorHasMore || actorLoading || actorPosts.length === 0) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && actorHasMore && !actorLoading) fetchActorPage({ append: true });
    }, { root: null, rootMargin: "300px", threshold: 0 });
    obs.observe(actorLoadMoreRef.current);
    return () => obs.disconnect();
  }, [activeTab, actorHasMore, actorLoading, actorPosts.length]);

  // ----- chart fetchers -----
  async function fetchChatterChart() {
    setChatterChartLoading(true); setChatterChartError("");
    const payload = { metric: "timeseries", per: perBucket };
    const key = JSON.stringify(payload);
    if (chatterChartInflightKeyRef.current === key) { setChatterChartLoading(false); return; }
    chatterChartInflightKeyRef.current = key;
    try {
      const res = await fetch(INTEL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
      const { buckets = [] } = await res.json();
      setChatterChartData(fillAllTime(buckets, perBucket));
    } catch (e) { setChatterChartError(e.message); setChatterChartData([]); }
    finally { setChatterChartLoading(false); chatterChartInflightKeyRef.current = ""; }
  }

  async function fetchNewsChart() {
    setNewsChartLoading(true); setNewsChartError("");
    const payload = { metric: "timeseries", per: perBucket };
    const key = JSON.stringify(payload);
    if (newsChartInflightKeyRef.current === key) { setNewsChartLoading(false); return; }
    newsChartInflightKeyRef.current = key;
    try {
      const res = await fetch(NEWS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
        body: JSON.stringify(payload),
      });
      if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
      const { buckets = [] } = await res.json();
      setNewsChartData(fillAllTime(buckets, perBucket));
    } catch (e) { setNewsChartError(e.message); setNewsChartData([]); }
    finally { setNewsChartLoading(false); newsChartInflightKeyRef.current = ""; }
  }

  async function fetchIocChart() {
      setIocChartLoading(true); setIocChartError("");
      const payload = { metric: "timeseries", per: perBucket };
      const key = JSON.stringify(payload);
      if (iocChartInflightKeyRef.current === key) { setIocChartLoading(false); return; }
      iocChartInflightKeyRef.current = key;
      try {
        const res = await fetch(IOC_API, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwtToken()}` },
            body: JSON.stringify(payload),
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error("Redirect"); }
        if (!res.ok) throw new Error("Failed to load chart");
        const { buckets = [] } = await res.json();
        setIocChartData(fillAllTime(buckets, perBucket));
      } catch (e) { setIocChartError(e.message); setIocChartData([]); }
      finally { setIocChartLoading(false); iocChartInflightKeyRef.current = ""; }
  }

  useEffect(() => {
    if (activeTab === "chatters") fetchChatterChart();
    else if (activeTab === "news") fetchNewsChart();
    else if (activeTab === "ioc") fetchIocChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, perBucket]);

  // ----- search handlers -----
  const applySearch = () => setQ((s) => (s === qInput ? s : qInput));
  const clearSearch = () => { setQInput(""); setQ(""); };

  // derived data
  const loadingList = activeTab === "actors" ? actorLoading : activeTab === "chatters" ? chatterLoading : activeTab === "news" ? newsLoading : iocLoading;
  const list = activeTab === "actors" ? actorPosts : activeTab === "chatters" ? chatterPosts : activeTab === "news" ? newsPosts : iocPosts;
  const error = activeTab === "actors" ? actorError : activeTab === "chatters" ? chatterError : activeTab === "news" ? newsError : iocError;
  const chartData = activeTab === "chatters" ? chatterChartData : activeTab === "news" ? newsChartData : iocChartData;
  const loadingChart = activeTab === "chatters" ? chatterChartLoading : activeTab === "news" ? newsChartLoading : iocChartLoading;
  const chartError = activeTab === "chatters" ? chatterChartError : activeTab === "news" ? newsChartError : iocChartError;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Mobile menu button */}


      {/* Header */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{
          marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 ? (collapsed ? "56px" : "224px") : "0"
        }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 text-slate-300 hover:text-white transition-colors"
            >
              <Menu size={24} />
            </button>

            <h1 className="text-[20px] font-bold leading-tight tracking-tight text-slate-100">
              Threat Feeds
            </h1>
          </div>

          {/* API add-on sales chip — backend decides visibility, hidden
              when user already owns the add-on. */}
          <ThreatFeedApiCta />
        </div>
      </header>

      {/* Sub-nav: tabs sit BELOW the header line, not inside it.
          marginLeft matches the persistent sidebar width on every viewport
          (sidebar isn't actually drawered on mobile yet), otherwise the
          collapsed rail covers the dropdown's left edge. */}
      <div
        className="border-b border-white/10 bg-[#000414]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 py-2">
          {/* MOBILE VIEW: Enterprise Dropdown */}
          <div className="sm:hidden w-full">
            <label htmlFor="tabs" className="sr-only">Select a view</label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                {activeTab === 'overview' && <Activity size={16} className="text-sky-500" />}
                {activeTab === 'chatters' && <Globe size={16} className="text-sky-500" />}
                {activeTab === 'actors' && <Users size={16} className="text-sky-500" />}
                {activeTab === 'news' && <Zap size={16} className="text-sky-500" />}
                {activeTab === 'ioc' && <ShieldAlert size={16} className="text-sky-500" />}
              </div>
              <select
                id="tabs"
                name="tabs"
                className="block w-full appearance-none rounded-md border py-2.5 pl-10 pr-10 text-sm font-medium focus:outline-none focus:ring-1 transition-colors"
                style={{
                  backgroundColor: "#000414",
                  borderColor: "#1e293b",
                  color: "#f1f5f9",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.5)"
                }}
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="overview">Overview Dashboard</option>
                <option value="chatters">Dark Web Chatters</option>
                <option value="actors">Threat Actor Directory</option>
                <option value="news">Public News</option>
                <option value="ioc">IOC Feeds</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* DESKTOP VIEW: Horizontal Tabs — thin vertical dividers between items */}
          <div className="hidden sm:flex items-center gap-5 flex-wrap">
            {[
              { id: "overview", label: "Overview" },
              { id: "chatters", label: "Dark Web Chatters" },
              { id: "actors", label: "Threat Actor Directory" },
              { id: "news", label: "Public News" },
              { id: "ioc", label: "IOC Feeds" },
            ].map((tab, i) => (
              <Fragment key={tab.id}>
                {i > 0 && <span aria-hidden className="h-3.5 w-px bg-neutral-700/60 -mb-px" />}
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
                    activeTab === tab.id
                      ? "text-white border-neutral-200"
                      : "text-neutral-400 hover:text-white border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="threatfeed"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pt-5 pb-8">

          {activeTab === "overview" ? (
    /* 1. SHOW DASHBOARD */
    <div className="max-w-8xl">
      <ThreatFeedDashboard />
    </div>
  ) : (
    /* 2. SHOW LISTS (Chatters, News, IOC, Actors) */
    <>
          {/* Toolbar */}
          <div className="max-w-8xl relative z-[120]">
            {/* Search row */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="relative flex-1 min-w-[240px] max-w-2xl">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                <input
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applySearch()}
                  placeholder={
                    activeTab === "chatters"
                      ? "Search title / content / organization / site / actors…"
                      : activeTab === "news"
                      ? "Search news titles and summaries…"
                      : activeTab === "ioc"
                      ? "Search IOC value or malware…"
                      : "Search actor name…"
                  }
                  className="w-full rounded-md border border-sky-900/40 bg-[#06101e] pl-8 pr-3 py-1.5 text-[13px] text-neutral-100 placeholder:text-neutral-500 outline-none focus:border-sky-500/60 transition-colors"
                />
              </div>

              <button
                type="button"
                onClick={applySearch}
                className="inline-flex items-center gap-1 rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[12px] font-medium text-sky-100 transition-colors"
              >
                Search
              </button>

              {(q || qInput) && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 hover:bg-white/[0.04] px-2.5 py-1.5 text-[12px] text-neutral-300 hover:text-white transition-colors"
                  title="Clear search"
                >
                  <X className="h-3 w-3" />
                  Clear
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  clearSearch();
                  resetFilters();
                  setDateQuick("30d");
                }}
                className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 hover:bg-white/[0.04] px-2.5 py-1.5 text-[12px] text-neutral-300 hover:text-white transition-colors"
                title="Reset search & filters"
              >
                Reset All
              </button>

              {/* Filter toggle for mobile */}
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden inline-flex items-center gap-1 rounded-md border border-sky-900/40 hover:bg-white/[0.04] px-2.5 py-1.5 text-[12px] text-neutral-300 hover:text-white ml-auto"
              >
                {showFilters ? "Hide" : "Show"} Filters
              </button>

              {activeTab === "ioc" && (
                <button
                  type="button"
                  onClick={() => setIntegrateOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[12px] font-medium text-sky-100 transition-colors ml-auto lg:ml-0"
                  title="Integrate firewall / proxy / SIEM using IOC downloads"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Integrate Firewall</span>
                  <span className="sm:hidden">Integrate</span>
                  <Zap className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter bar — per sub-tab */}
            <div className={`${showFilters ? "block" : "hidden"} lg:block`}>
              {activeTab === "chatters" ? (
                <FilterBar>
                  <SearchableSelect
                    label="Network"
                    options={networkOpts}
                    value={network}
                    onChange={(v) => setNetwork(v)}
                    includeAll
                    allValue="all"
                    allLabel="All"
                    placeholder="All networks"
                    loading={dimLoading}
                    error={dimError}
                    menuWidthClass="w-80"
                  />
                  <SearchableSelect
                    label="Category"
                    options={categoryOpts}
                    value={category || ""}
                    onChange={(v) => setCategory(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="All categories"
                    menuWidthClass="w-80"
                  />
                  <SearchableSelect
                    label="Industry"
                    options={industryOpts}
                    value={industry}
                    onChange={(v) => setIndustry(v)}
                    includeAll
                    allValue="all"
                    allLabel="All"
                    placeholder="All industries"
                    menuWidthClass="w-72"
                  />
                  <SearchableSelect
                    label="Actor"
                    options={actorOpts}
                    value={actor || ""}
                    onChange={(v) => setActor(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="All actors"
                    menuWidthClass="w-80"
                  />
                  <FilterDivider />
                  <FilterInput label="Country" value={country} onChange={setCountry} placeholder="e.g. Iraq" width="w-28" />
                  <FilterInput label="Domain" value={domain} onChange={setDomain} placeholder="victim domain" width="w-36" />
                  <FilterInput label="Site" value={site} onChange={setSite} placeholder="victim site" width="w-32" />
                  <FilterInput label="Tag" value={tag} onChange={setTag} placeholder="e.g. ddos" width="w-24" />
                  <FilterDivider />
                  <FilterSegment
                    label="Range"
                    value={dateQuick}
                    onChange={setDateQuick}
                    options={[
                      { key: "7d", label: "7d" },
                      { key: "30d", label: "30d" },
                      { key: "90d", label: "90d" },
                      { key: "All", label: "All" },
                    ]}
                  />
                  <FilterReset onClick={resetFilters} className="ml-auto" />
                </FilterBar>
              ) : activeTab === "ioc" ? (
                <FilterBar>
                  <SearchableSelect
                    label="IOC Type"
                    options={iocTypeOpts}
                    value={iocType || ""}
                    onChange={(v) => setIocType(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="IP, Domain…"
                    loading={dimLoading}
                    menuWidthClass="w-60"
                  />
                  <SearchableSelect
                    label="Threat"
                    options={threatTypeOpts}
                    value={threatType || ""}
                    onChange={(v) => setThreatType(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="Botnet, C2…"
                    menuWidthClass="w-60"
                  />
                  <SearchableSelect
                    label="Malware"
                    options={malwareOpts}
                    value={malware || ""}
                    onChange={(v) => setMalware(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="Cobalt Strike…"
                    menuWidthClass="w-60"
                  />
                  <SearchableSelect
                    label="Reporter"
                    options={reporterOpts}
                    value={reporter || ""}
                    onChange={(v) => setReporter(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="Reporter ID"
                    menuWidthClass="w-60"
                  />
                  <FilterReset onClick={resetFilters} className="ml-auto" />
                </FilterBar>
              ) : activeTab === "actors" ? (
                <FilterBar>
                  <FilterSegment
                    label="Status"
                    value={actorStatus}
                    onChange={setActorStatus}
                    options={[
                      { key: "", label: "All" },
                      { key: "Active", label: "Active" },
                      { key: "Dormant", label: "Dormant" },
                    ]}
                  />
                  <FilterMenu
                    label="TTPs"
                    value={actorTtp || null}
                    placeholder="All"
                    width="w-48"
                  >
                    {(close) =>
                      [
                        { v: "", l: "All TTPs" },
                        { v: "Ransomware", l: "Ransomware" },
                        { v: "Data Breach", l: "Data Breach" },
                        { v: "Data Leak", l: "Data Leak" },
                        { v: "Defacement", l: "Defacement" },
                        { v: "Initial Access", l: "Initial Access" },
                        { v: "DDoS Attack", l: "DDoS Attack" },
                      ].map((opt) => (
                        <FilterMenuItem
                          key={opt.v}
                          active={actorTtp === opt.v}
                          onClick={() => {
                            setActorTtp(opt.v);
                            close();
                          }}
                        >
                          {opt.l}
                        </FilterMenuItem>
                      ))
                    }
                  </FilterMenu>
                  <FilterDivider />
                  <FilterSegment
                    label="Sort"
                    value={actorSort}
                    onChange={setActorSort}
                    options={[
                      { key: "activity", label: "Most Active" },
                      { key: "recent", label: "Recently Seen" },
                      { key: "name", label: "Name" },
                    ]}
                  />
                  <FilterReset onClick={resetFilters} className="ml-auto" />
                </FilterBar>
              ) : (
                <FilterBar>
                  <FilterSegment
                    label="Range"
                    value={dateQuick}
                    onChange={setDateQuick}
                    options={[
                      { key: "7d", label: "7d" },
                      { key: "30d", label: "30d" },
                      { key: "90d", label: "90d" },
                      { key: "All", label: "All" },
                    ]}
                  />
                  <SearchableSelect
                    label="Tag"
                    options={NEWS_TAG_OPTIONS}
                    value={tag || ""}
                    onChange={(v) => setTag(v === "all" ? "" : v)}
                    includeAll
                    allValue=""
                    allLabel="All"
                    placeholder="All tags"
                    menuWidthClass="w-80"
                  />
                  <FilterReset
                    onClick={() => {
                      clearSearch();
                      setDateQuick("30d");
                    }}
                    className="ml-auto"
                  />
                </FilterBar>
              )}
            </div>
          </div>

          {/* Two-column layout - stack on mobile */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] lg:items-start gap-6 max-w-8xl">
            {/* LEFT: list */}
            <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-sky-700 scrollbar-track-transparent relative z-[10]">
              {loadingList && <div className={`rounded-md ${frosted} p-8`}>Loading…</div>}
              {error && !loadingList && <div className={`rounded-md ${frosted} p-8 text-red-300`}>{error}</div>}
              {!loadingList && !error && list.length === 0 ? (
                <div className={`rounded-md ${frosted} p-8`}>
                  <div className="text-lg font-medium">No results found</div>
                  <div className="mt-1 text-sm text-neutral-400">Try another search keyword or filter.</div>
                </div>
              ) : (
                list.map((p, idx) =>
                  activeTab === "chatters" ? (
                    <button
                      key={`chat-${p.id}`}
                      onClick={() => { setSelectedId(p.id); setDetailOpen(true); }}
                      className={`group text-left w-full rounded-md ${frosted} p-4 sm:p-5 transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-sky-500/40`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-neutral-400">{fmtISO(p.published_at)}</div>
                      </div>
                      <div className="mt-2 text-base sm:text-lg md:text-xl font-semibold leading-snug break-words">{p.title || "(untitled)"}</div>
                      {Array.isArray(p.tags) && p.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {p.tags.map((t, i) => (
                            <span key={`${t}-${i}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${tagClass(String(t).toLowerCase())}`}>{t}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ) : activeTab === "news" ? (
                    <button
                      key={`news-${p.id}`}
                      onClick={() => { setSelectedNewsId(p.id); setNewsDetailOpen(true); }}
                      className={`group text-left w-full rounded-md ${frosted} p-4 sm:p-5 transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-sky-500/40`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs text-neutral-400">{fmtISO(p.published_at)}</div>
                        {p.source && <div className="text-xs text-neutral-400">{p.source}</div>}
                      </div>
                      <div className="mt-2 text-base sm:text-lg md:text-xl font-semibold leading-snug break-words">{p.title || "(untitled)"}</div>
                      {Array.isArray(p.tags) && p.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {p.tags.map((t, i) => (
                            <span key={`${t}-${i}`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${tagClass(String(t).toLowerCase())}`}>{t}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ) : activeTab === "actors" ? (
                    <button key={`actor-${idx}`} onClick={() => { setSelectedActorSlug(p.slug); setActorDetailOpen(true); }} className={`group w-full text-left rounded-md ${frosted} p-4 sm:p-5 transition hover:bg-white/5`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex items-center gap-3 flex-wrap">
                                <Users className="h-5 w-5 text-sky-400 shrink-0" />
                                <div className="text-base sm:text-lg font-bold text-white break-words">{p.name}</div>
                                {p.status === "Active" ? (
                                    <span className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px] uppercase font-bold px-2 py-0.5 rounded">Active</span>
                                ) : (
                                    <span className="bg-neutral-500/10 text-neutral-400 border border-neutral-500/20 text-[10px] uppercase font-bold px-2 py-0.5 rounded">Dormant</span>
                                )}
                            </div>
                            <div className="text-xs text-neutral-400 flex items-center gap-1">
                                <Activity className="h-3 w-3" /> Seen {fmtISO(p.last_seen)}
                            </div>
                        </div>
                        <div className="mt-4 flex items-center gap-4 sm:gap-6 text-sm text-neutral-300">
                             <div className="flex flex-col">
                                 <span className="text-xs text-neutral-500 uppercase">Recorded Incidents</span>
                                 <span className="font-mono text-white text-lg">{p.incidents}</span>
                             </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {p.attack_types.map(t => (
                                <span key={t} className="text-[10px] border border-red-500/30 bg-red-500/10 text-red-200 px-2 py-0.5 rounded-full">{t}</span>
                            ))}
                        </div>
                    </button>
                  ) : (
                    <button
                      key={`ioc-${p.id}`}
                      onClick={() => { setSelectedIocId(p.id); setIocDetailOpen(true); }}
                      className={`group text-left w-full rounded-md ${frosted} p-4 sm:p-5 transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-sky-500/40`}
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                             <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${iocTagClass(p.ioc_type)}`}>{p.ioc_type}</span>
                                <span className="text-xs text-neutral-400">{fmtISO(p.first_seen)}</span>
                             </div>
                             {p.threat_type && <div className="text-xs font-mono text-sky-400">{p.threat_type}</div>}
                        </div>
                        <div className="text-sm sm:text-base md:text-lg font-mono font-bold text-white break-all">{p.ioc_value}</div>
                        {p.malware && (
                             <div className="mt-2 flex items-center gap-2">
                                 <Bug className="h-3 w-3 text-red-400 shrink-0" />
                                 <span className="text-xs font-bold text-red-300 uppercase tracking-wide">{p.malware}</span>
                             </div>
                        )}
                    </button>
                  )
                )
              )}
              <div ref={activeTab === "chatters" ? chatterLoadMoreRef : activeTab === "news" ? newsLoadMoreRef : activeTab === "ioc" ? iocLoadMoreRef : actorLoadMoreRef} className="h-8" />
            </div>

            {/* RIGHT: chart card */}
            <div className={`rounded-md ${frosted} p-4 sm:p-5 flex flex-col`}>
              
              {activeTab === "actors" ? (
                (() => {
                  const total = actorStats?.total_actors || 0;
                  const active = actorStats?.active_actors || 0;
                  const dormant = Math.max(0, total - active);
                  const incidents = actorStats?.tracked_incidents || 0;
                  const activePct = total > 0 ? Math.round((active / total) * 100) : 0;
                  const dormantPct = total > 0 ? 100 - activePct : 0;
                  return (
                    <div className="flex flex-col">
                      <div className="text-[13px] font-semibold text-neutral-100 uppercase tracking-wider">
                        Directory Snapshot
                      </div>

                      {!actorStats ? (
                        <div className="mt-4 text-[12px] text-neutral-500">Loading…</div>
                      ) : (
                        <>
                          {/* Stat strip — three compact tiles, single accent tone.
                              Labels spell out *what* each metric counts so users
                              don't have to guess at "Active / Tracked / etc." */}
                          <div className="mt-3 grid grid-cols-3 divide-x divide-sky-900/30 border border-sky-900/30 rounded-md overflow-hidden">
                            <div className="px-3 py-3">
                              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">Threat Groups</div>
                              <div className="mt-1 text-[22px] leading-none font-semibold text-neutral-100 tabular-nums">
                                {total.toLocaleString()}
                              </div>
                              <div className="mt-0.5 text-[10.5px] text-neutral-500">in directory</div>
                            </div>
                            <div className="px-3 py-3">
                              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">Active Actors</div>
                              <div className="mt-1 text-[22px] leading-none font-semibold text-emerald-300 tabular-nums">
                                {active.toLocaleString()}
                              </div>
                              <div className="mt-0.5 text-[10.5px] text-neutral-500">active in last 90 days</div>
                            </div>
                            <div className="px-3 py-3">
                              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">Victims</div>
                              <div className="mt-1 text-[22px] leading-none font-semibold text-neutral-100 tabular-nums">
                                {incidents.toLocaleString()}
                              </div>
                              <div className="mt-0.5 text-[10.5px] text-neutral-500">confirmed claimed</div>
                            </div>
                          </div>

                          {/* Status distribution bar */}
                          <div className="mt-5">
                            <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
                              <span>Active vs. Dormant Groups</span>
                              <span className="text-neutral-600 normal-case tracking-normal">
                                {active.toLocaleString()} of {total.toLocaleString()} posting
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-neutral-800/70 overflow-hidden flex">
                              <div
                                className="bg-emerald-500/70 h-full transition-all"
                                style={{ width: `${activePct}%` }}
                              />
                              <div
                                className="bg-neutral-600/50 h-full transition-all"
                                style={{ width: `${dormantPct}%` }}
                              />
                            </div>
                            <div className="mt-2 flex items-center gap-4 text-[11px]">
                              <span className="inline-flex items-center gap-1.5 text-neutral-300">
                                <span className="h-2 w-2 rounded-sm bg-emerald-500/70" />
                                Active (≤90 days) <span className="text-neutral-500 tabular-nums">{activePct}%</span>
                              </span>
                              <span className="inline-flex items-center gap-1.5 text-neutral-300">
                                <span className="h-2 w-2 rounded-sm bg-neutral-600/70" />
                                Dormant (&gt;90 days) <span className="text-neutral-500 tabular-nums">{dormantPct}%</span>
                              </span>
                            </div>
                          </div>

                          {/* Most active — top 5 from the current list */}
                          {Array.isArray(list) && list.length > 0 && (
                            <div className="mt-5">
                              <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
                                <span>Most Active Groups</span>
                                <span className="text-neutral-600 normal-case tracking-normal">by victim count</span>
                              </div>
                              <div className="border border-sky-900/30 rounded-md overflow-hidden">
                                {list.slice(0, 5).map((a, i) => (
                                  <button
                                    key={a.slug || i}
                                    type="button"
                                    onClick={() => {
                                      setSelectedActorSlug(a.slug);
                                      setActorDetailOpen(true);
                                    }}
                                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors border-b border-sky-900/20 last:border-b-0 ${
                                      i % 2 === 0 ? "" : "bg-[#091020]/30"
                                    } hover:bg-sky-900/15`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-neutral-500 text-[11px] tabular-nums w-4 shrink-0">
                                        {i + 1}
                                      </span>
                                      <span className="text-[12.5px] text-neutral-200 truncate">{a.name}</span>
                                      {a.status === "Active" ? (
                                        <span className="shrink-0 text-[9.5px] uppercase tracking-wider px-1 py-px rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                                          Active
                                        </span>
                                      ) : (
                                        <span className="shrink-0 text-[9.5px] uppercase tracking-wider px-1 py-px rounded bg-neutral-500/10 text-neutral-400 border border-neutral-500/30">
                                          Dormant
                                        </span>
                                      )}
                                    </div>
                                    {typeof a.victim_count === "number" && (
                                      <span className="shrink-0 text-[11.5px] text-sky-300 tabular-nums">
                                        {a.victim_count.toLocaleString()}
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Footer — discrete, single-line definition */}
                          <div className="mt-4 pt-3 border-t border-sky-900/20 text-[11px] text-neutral-500 leading-relaxed">
                            <span className="text-neutral-300">Dormant</span> groups have not been observed posting new victims in the last 90 days.
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()
              ) : (
                  <>
                      <div className="flex items-center justify-between">
                        <div className="text-xl sm:text-2xl font-semibold">
                          {activeTab === "chatters" ? "Intel Activity" : activeTab === "news" ? "News Activity" : "IOC Volumetrics"}
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="text-base sm:text-lg font-semibold">
                          {activeTab === "chatters" ? "Posts Over Time" : activeTab === "news" ? "Articles Over Time" : "Indicators Seen Over Time"}
                        </div>
                        <div className="h-[2px] w-12 rounded bg-white/20 mt-1" />
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-neutral-300">
                        <span>Range: <strong>All-time</strong></span>
                        <span>Bucket: <strong>{perBucket}</strong></span>
                        {loadingChart && <span className="opacity-70">Updating…</span>}
                        {chartError && <span className="text-red-400 text-sm">{chartError}</span>}
                      </div>

                      <div className="mt-3 h-[300px] sm:h-[360px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 12 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                            <XAxis 
                              dataKey="label" 
                              stroke="#94a3b8" 
                              tickLine={false} 
                              axisLine={{ stroke: "#334155" }}
                              tick={{ fontSize: 12 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis 
                              stroke="#94a3b8" 
                              tickLine={false} 
                              axisLine={{ stroke: "#334155" }} 
                              allowDecimals={false}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                              cursor={{ fill: "rgba(56,189,248,0.10)" }}
                              content={({ active, payload, label }) =>
                                active && payload?.length ? (
                                  <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs text-neutral-200 shadow-xl">
                                    <div className="mb-1 text-neutral-400">{label}</div>
                                    <div className="font-semibold text-white">{payload[0].value}</div>
                                  </div>
                                ) : null
                              }
                            />
                            <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#3b82f6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                  </>
              )}
            </div>
          </div>
                </>
  )}
        </main>
  
      </div>

      {/* Integrate Firewall Modal (IOC) */}
      {integrateOpen && activeTab === "ioc" && (
        <div className="fixed inset-0 z-[999]">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIntegrateOpen(false)}
          />

          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={`w-full max-w-2xl rounded-md ${frosted} border border-sky-700/30 max-h-[90vh] overflow-hidden flex flex-col`}>
              {/* header */}
              <div className="flex items-start justify-between border-b border-white/10 px-4 sm:px-5 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-sky-300" />
                    <h2 className="text-lg sm:text-xl font-semibold">Firewall Integration</h2>
                  </div>
                  <p className="mt-1 text-xs sm:text-sm text-neutral-400">
                    Generate secure download links for IOC feeds.
                  </p>
                </div>

                <button
                  onClick={() => setIntegrateOpen(false)}
                  className="rounded-lg p-2 text-neutral-300 hover:text-white hover:bg-white/5"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* body - scrollable */}
              <div className="px-4 sm:px-5 py-4 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-sky-700 scrollbar-track-transparent">
                {/* token section */}
                <div className="rounded-md border border-white/10 bg-black/20 p-4">
                  <div className="flex flex-col gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">Threat Feed Token</div>
                      <div className="text-xs text-neutral-400">
                        Used to authorize feed downloads for your license.
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={fetchThreatFeedToken}
                        disabled={tfTokenLoading}
                        className={`rounded-lg px-3 py-1.5 text-sm border transition ${
                          tfTokenLoading
                            ? "border-white/10 text-neutral-400 cursor-not-allowed"
                            : "border-sky-700/60 hover:bg-white/5"
                        }`}
                      >
                        {tfTokenLoading ? "Retrieving…" : tfToken ? "Refresh Token" : "Get Token"}
                      </button>

                      {tfToken && (
                        <button
                          onClick={async () => {
                            await copyToClipboard(tfToken);
                          }}
                          className="rounded-lg px-3 py-1.5 text-sm border border-sky-700/60 hover:bg-white/5"
                          title="Copy token"
                        >
                          Copy
                        </button>
                      )}
                    </div>

                    {tfTokenError && (
                      <div className="mt-3 text-sm text-red-300">
                        {tfTokenError}
                      </div>
                    )}

                    {tfToken && (
                      <div className="mt-3">
                        <div className="text-xs text-neutral-400 mb-1">Token</div>
                        <div className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-sky-200 break-all">
                          {tfToken}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* instructions */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="rounded-md border border-white/10 bg-black/20 p-4">
                    <div className="text-sm font-semibold">How it works</div>
                    <ul className="mt-2 space-y-2 text-xs sm:text-sm text-neutral-300">
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
                        Download feed outputs are scoped to <span className="font-semibold text-white">last 24 hours</span>.
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
                        Choose IOC type (IP / URL / Domain / MD5 / SHA256).
                      </li>
                      <li className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
                        Use the generated URL in your firewall/proxy/EDR scheduled job.
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-md border border-white/10 bg-black/20 p-4">
                    <div className="text-sm font-semibold">Generate Download URL</div>

                    <div className="mt-3">
                      <label className="text-xs text-neutral-400">IOC Type</label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {["ip", "url", "domain", "md5", "sha256"].map((t) => (
                          <button
                            key={t}
                            onClick={() => setDownloadType(t)}
                            className={`${chip} ${
                              downloadType === t
                                ? "border-sky-500/40 bg-sky-500/10 text-white"
                                : "border-sky-900/30 bg-transparent text-neutral-300 hover:bg-neutral-900/40"
                            }`}
                          >
                            {t.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <label className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Time Window</label>
                        <span className="text-[10px] text-neutral-500">
                          {downloadTime === '24h' || downloadTime === '48h' ? '(Recommended for blocking)' : '(Recommended for detection)'}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[
                          { val: "24h", label: "24h" },
                          { val: "48h", label: "48h" },
                          { val: "72h", label: "72h" },
                          { val: "7d", label: "7d" },
                          { val: "30d", label: "30d" },
                        ].map((opt) => (
                          <button
                            key={opt.val}
                            onClick={() => setDownloadTime(opt.val)}
                            className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                              downloadTime === opt.val
                                ? "border-sky-500 bg-sky-500/20 text-sky-100 shadow-[0_0_10px_rgba(14,165,233,0.3)]"
                                : "border-white/10 bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5">
                      <label className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Endpoint URL</label>
                      <div className="mt-2 rounded-lg border border-sky-500/30 bg-[#06101e] p-0 overflow-hidden group">
                        
                        <div className="p-3 font-mono text-xs text-sky-300 break-all bg-black/20">
                          {tfToken
                            ? buildDownloadUrl(downloadType, downloadTime)
                            : "⚠️ Retrieve a token above to generate the URL."}
                        </div>

                        <div className="flex items-center gap-px border-t border-white/5 bg-white/5">
                          <button
                            disabled={!tfToken}
                            onClick={async () => {
                              if (!tfToken) return;
                              await copyToClipboard(buildDownloadUrl(downloadType, downloadTime));
                            }}
                            className={`flex-1 py-2 text-xs font-medium transition hover:bg-white/10 ${
                              !tfToken ? "text-neutral-600 cursor-not-allowed" : "text-white"
                            }`}
                          >
                            Copy URL
                          </button>
                          <div className="w-px h-4 bg-white/10"></div>
                          <a
                            href={tfToken ? buildDownloadUrl(downloadType, downloadTime) : undefined}
                            target="_blank"
                            rel="noreferrer"
                            className={`flex-1 py-2 text-center text-xs font-medium transition hover:bg-white/10 ${
                              !tfToken ? "text-neutral-600 pointer-events-none" : "text-white"
                            }`}
                          >
                            Test in Browser
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-xs text-neutral-400">
                      Tip: Run this every 15–60 minutes in your automation to keep rules fresh.
                    </div>
                  </div>
                </div>

                {/* sample integration */}
                <div className="rounded-md border border-white/10 bg-black/20 p-4">
                  <div className="text-sm font-semibold">Example: Cron / Scheduled Job</div>
                  <div className="mt-2 text-xs text-neutral-400">
                    Download and apply IOC updates periodically.
                  </div>

                  <div className="mt-3 rounded-lg border border-white/10 bg-black/40 p-3">
                    <div className="text-xs text-neutral-400 mb-2">Example (curl):</div>
                    <pre className="text-xs text-sky-200 font-mono whitespace-pre-wrap break-all">
{`curl -L "${tfToken ? buildDownloadUrl(downloadType, downloadTime) : "https://.../download_feed.php?token=<token>&type=<type>&time=<time>"}" -o iocs.txt`}
                    </pre>
                  </div>
                </div>
              </div>

              {/* footer */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-white/10 px-4 sm:px-5 py-3">
                <div className="text-xs text-neutral-400">
                  Requires appropriate license access for IOC feeds.
                </div>
                <button
                  onClick={() => setIntegrateOpen(false)}
                  className="rounded-lg px-4 py-2 text-sm border border-sky-700/60 hover:bg-white/5"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail slide-overs */}
      <IntelDetailSidebar
        open={detailOpen && activeTab === "chatters"}
        onClose={() => setDetailOpen(false)}
        postId={selectedId}
        jwtToken={getJwtToken()}
        apiUrl={INTEL_API}
      />
      <IntelNewsDetailSidebar
        open={newsDetailOpen && activeTab === "news"}
        onClose={() => setNewsDetailOpen(false)}
        newsId={selectedNewsId}
        jwtToken={getJwtToken()}
        apiUrl={NEWS_API}
      />
      <DetailSidebarIOC
        open={iocDetailOpen && activeTab === "ioc"}
        onClose={() => setIocDetailOpen(false)}
        iocId={selectedIocId}
        jwtToken={getJwtToken()}
        apiUrl={IOC_API}
      />
      <DetailSidebarActor 
        open={actorDetailOpen && activeTab === "actors"} 
        onClose={() => setActorDetailOpen(false)} 
        slug={selectedActorSlug} 
        jwtToken={getJwtToken()} 
        apiUrl={ACTOR_API} 
      />
    </div>
  );
}